<script setup>
import { ref, watch, nextTick } from 'vue';
import { useToggle } from '@vueuse/core';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  filters: {
    type: Array,
    required: true,
  },
  visibleFilters: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['update:visibleFilters']);

const [showMenu, toggleMenu] = useToggle(false);
const buttonRef = ref(null);
const menuStyle = ref({});

const updateMenuPosition = () => {
  if (buttonRef.value) {
    const rect = buttonRef.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const menuWidth = 280;

    let left = rect.right - menuWidth;
    if (left < 8) {
      left = rect.left;
    }

    menuStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${left}px`,
      width: `${menuWidth}px`,
      zIndex: 9999,
    };
  }
};

watch(showMenu, async isVisible => {
  if (isVisible) {
    await nextTick();
    updateMenuPosition();
  }
});

const toggleFilter = filterKey => {
  const newVisibleFilters = [...props.visibleFilters];
  const index = newVisibleFilters.indexOf(filterKey);

  if (index > -1) {
    newVisibleFilters.splice(index, 1);
  } else {
    newVisibleFilters.push(filterKey);
  }

  emit('update:visibleFilters', newVisibleFilters);
};

const isFilterVisible = filterKey => {
  return props.visibleFilters.includes(filterKey);
};

const resetToDefaults = () => {
  const defaultFilters = props.filters.map(filter => filter.key);
  emit('update:visibleFilters', defaultFilters);
};
</script>

<template>
  <div ref="buttonRef" class="relative">
    <Button
      slate
      outline
      size="sm"
      icon="i-lucide-filter"
      @click="toggleMenu()"
    >
      Filtros
    </Button>

    <teleport to="body">
      <OnClickOutside @trigger="toggleMenu(false)">
        <div
          v-if="showMenu"
          :style="menuStyle"
          class="box-border border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] fixed shadow-lg border-n-strong dark:border-n-strong p-4 z-[9999]"
        >
          <div class="flex items-center justify-between mb-4">
            <h4 class="m-0 text-sm font-medium text-n-slate-12">
              Filtros Visíveis
            </h4>
            <Button
              ghost
              slate
              xs
              icon="i-lucide-x"
              @click="toggleMenu(false)"
            />
          </div>

          <div class="space-y-2 max-h-64 overflow-y-auto mb-4">
            <label
              v-for="filter in filters"
              :key="filter.key"
              class="flex items-center space-x-2 cursor-pointer hover:bg-n-solid-4 p-2 rounded"
            >
              <input
                type="checkbox"
                :checked="isFilterVisible(filter.key)"
                class="w-4 h-4 text-blue-600 border-n-weak rounded focus:ring-blue-500"
                @change="toggleFilter(filter.key)"
              />
              <span class="text-sm text-n-slate-12">{{ filter.label }}</span>
            </label>
          </div>

          <div class="pt-3 border-t border-n-weak">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              class="w-full"
              @click="resetToDefaults"
            >
              Restaurar Padrões
            </Button>
          </div>
        </div>
      </OnClickOutside>
    </teleport>
  </div>
</template>
